'use client'

import { useEffect, useState } from 'react'

/**
 * Deterministic-then-local date rendering, to avoid SSR hydration mismatches
 * WITHOUT forcing every viewer onto UTC / en-US.
 *
 * The problem: `new Date(iso).toLocaleDateString()` uses the runtime's locale
 * + timezone. The SSR server (UTC / default locale) and the client (the
 * viewer's) then produce different strings, so the date hydrates differently
 * and React logs a hydration error (Sentry THEDUCHY-WEBSITE-8).
 *
 * The fix: render a deterministic value (en-US / UTC) on the server AND the
 * first client (hydration) render — so they match — then re-render in the
 * viewer's own locale + timezone once mounted. Net result: no hydration
 * mismatch, and the user still sees their local date/time (a brief first
 * paint may show the UTC value before it settles to local).
 */

/** True once mounted on the client. */
export function useMounted(): boolean {
  const [mounted, setMounted] = useState(false)
  // Intentional: flip to true exactly once after mount to trigger the
  // swap from the deterministic (SSR-safe) value to the viewer's local one.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), [])
  return mounted
}

const DET_LOCALE = 'en-US'
const DET_TZ = 'UTC'
const DEFAULT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
}

/** Format a date string. `mounted` (from useMounted) false → deterministic
 *  (en-US / UTC); true → the viewer's locale + timezone. */
export function formatLocalDate(
  iso: string | null | undefined,
  mounted: boolean,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTS,
): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return mounted
    ? d.toLocaleDateString(undefined, options)
    : d.toLocaleDateString(DET_LOCALE, { ...options, timeZone: DET_TZ })
}

/** Same as formatLocalDate but includes the time (toLocaleString). */
export function formatLocalDateTime(
  iso: string | null | undefined,
  mounted: boolean,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return mounted
    ? d.toLocaleString(undefined, options)
    : d.toLocaleString(DET_LOCALE, { ...options, timeZone: DET_TZ })
}

/**
 * Convenience hook: returns `{ date, dateTime }` formatters bound to the
 * component's mounted state. Use when you need the formatted STRING (e.g.
 * inside a translation, a chart label, or a prop) rather than JSX — call the
 * hook once at the top of the component, then `fmt.date(iso)` anywhere.
 */
export function useLocalDateFormatter() {
  const mounted = useMounted()
  return {
    date: (iso: string | null | undefined, options?: Intl.DateTimeFormatOptions) =>
      formatLocalDate(iso, mounted, options),
    dateTime: (iso: string | null | undefined, options?: Intl.DateTimeFormatOptions) =>
      formatLocalDateTime(iso, mounted, options),
  }
}

/**
 * Drop-in for rendering a date inline in JSX. Owns its own mounted state, so
 * it can be used anywhere without threading `useMounted` through the parent.
 * `suppressHydrationWarning` is belt-and-suspenders — the deterministic first
 * render already matches the server.
 */
export function ClientDate({
  iso,
  mode = 'date',
  options,
}: {
  iso: string | null | undefined
  mode?: 'date' | 'datetime'
  options?: Intl.DateTimeFormatOptions
}) {
  const mounted = useMounted()
  const text =
    mode === 'datetime'
      ? formatLocalDateTime(iso, mounted, options)
      : formatLocalDate(iso, mounted, options)
  return <span suppressHydrationWarning>{text}</span>
}
